import { Component, ComponentRef, OnInit, ViewChild, ViewContainerRef, ComponentFactoryResolver, ViewEncapsulation } from '@angular/core';
import { ConfigurationService } from '../../../services/configuration/configuration.service';
import { ChatbotComponent } from '../chatbot/chatbot.component';
import { ChatbotOldComponent } from '../chatbot-old/chatbot-old.component';
import { AgentWorkspaceComponent } from '../../agents/components/agent-workspace/agent-workspace.component';
import { AutoResizeTextareaDirective } from '../../../directives/textarea/auto-resize-textarea.directive';
import { ChatbotService } from 'src/services/chatbot-services/chatbot.service';
import { environment } from 'src/environments/environment';
import { PrivilegesService } from 'src/services/privileges-service/privileges.service';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import config from 'src/config/config.json';
import { ContenteditableModelDirective } from 'src/directives/contenteditable-textarea/contenteditable-model.directive';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { AgentAccessControlService } from '../../agents/services/agent-access-control/agent-access-control.service';
import { AgentHubService } from '../../agents/services/agent-hub/agent-hub.service';
import { RegulationNextService } from '../../../services/regulation-next/regulation-next.service';
import { WorkbenchAccessService } from '../../../services/workbench-access/workbench-access.service';
import { BrandThemeService } from '../../../services/brand-theme/brand-theme.service';
import {
  WORKBENCH_DEEP_LINK_QUERY_KEY,
  WORKBENCH_DEEP_LINK_ROUTE_VALUE
} from '../../agents/constants/workbench-post-message';
@Component({
  selector: 'app-chatbot-wrapper',
  templateUrl: './chatbot-wrapper.component.html',
  styleUrls: ['./chatbot-wrapper.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ChatbotWrapperComponent implements OnInit {
  @ViewChild('container', { read: ViewContainerRef, static: false }) container!: ViewContainerRef;
  isLoading = true;
  hasAuthError = false;
  supportedLanguages: string[] = config.SUPPORTED_LANGUAGES;
  private readonly regulationsAgentId = 'regulations_agent';
  private readonly regulationsModuleName = 'Regulations';
  private readonly ChatbotWrapperComponentError = (error: any)=>
    `[ChatbotWrapperComponent] ${JSON.stringify(error)}`;
  constructor(
    public configService: ConfigurationService,
    private readonly resolver: ComponentFactoryResolver,
    private readonly chatbotService: ChatbotService,
    private readonly privilegesService: PrivilegesService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly loggerService: LoggerService,
    private readonly agentAccessControlService: AgentAccessControlService,
    private readonly agentHubService: AgentHubService,
    private readonly regulationNextService: RegulationNextService,
    private readonly workbenchAccessService: WorkbenchAccessService,
    private readonly brandThemeService: BrandThemeService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      console.log('[ChatbotWrapper] ========== APPLICATION START ==========');
      
      // ========================================
      // STEP 1: Load Configuration & Feature Flags
      // ========================================
      await this.configService.loadConfiguration();
      await this.configService.loadFeatureFlags();
      
      const authError = this.configService.hasFeatureConfigAuthError();
      if (authError) {
        console.error('[ChatbotWrapper] Authentication error detected');
        this.hasAuthError = true;
        this.isLoading = false;
        return;
      }

      // ========================================
      // STEP 2: Check Tenant Existence
      // ========================================
      const featureFlags: any = this.configService.featureFlag;
      const tenantExists = featureFlags?.['configuration']?.[environment.TenantId] !== undefined;
      
      console.log('[ChatbotWrapper] Tenant check:', {
        tenantId: environment.TenantId,
        exists: tenantExists
      });
      
      if (!tenantExists) {
        console.log('[ChatbotWrapper] ❌ No tenant found → Loading v1.x');
        await this.loadV1Component();
        return;
      }

      // ========================================
      // STEP 3: PRIMARY CHECK - Agentic UI
      // ========================================
      const agenticUIEnabled = await this.configService.isAgenticUIEnabled();
      
      console.log('[ChatbotWrapper] PRIMARY CHECK - Agentic UI:', agenticUIEnabled);
      
      if (agenticUIEnabled) {
        console.log('[ChatbotWrapper] ✓ Agentic UI enabled → Checking for available agents');
        
        // Get enrolled agents (only those with value = true)
        const enrolledAgentIds = this.configService.getEnrolledAgentIds();
        
        console.log('[ChatbotWrapper] Enrolled agents (tenant-level):', enrolledAgentIds);
        
        if (enrolledAgentIds.length > 0) {
          console.log('[ChatbotWrapper] ✓ Found enrolled agents → Validating user ACL');
          
          // Get ACL-validated agents (agents that pass all 3 checks)
          const availableAgents = await this.agentAccessControlService.getAvailableAgents();
          const availableAgentIds = availableAgents.map(agent => agent.id);
          
          console.log('[ChatbotWrapper] ACL-validated agents (user-level):', availableAgentIds);
          
          // Calculate intersection (enrolled AND ACL-validated)
          const intersection = enrolledAgentIds.filter(agentId => 
            availableAgentIds.includes(agentId)
          );
          
          console.log('[ChatbotWrapper] Intersection (enrolled ∩ ACL):', intersection);
          
          if (intersection.length > 0) {
            console.log('[ChatbotWrapper] ✅ Loading v3.0 Agentic UI with agents:', intersection);
            this.configService.setReleaseVersion('3.0');
            await this.loadV3Component(intersection);
            return;
          } else {
            console.log('[ChatbotWrapper] ⚠️ No intersection → Checking v2.0 fallback');
            this.configService.setReleaseVersion('2.0');
          }
        } else {
          console.log('[ChatbotWrapper] ⚠️ No enrolled agents → Checking v2.0 fallback');
          this.configService.setReleaseVersion('2.0');
        }
      } else {
        console.log('[ChatbotWrapper] ✗ Agentic UI disabled → Checking v2.0 fallback');
        this.configService.setReleaseVersion('2.0');
      }

      // ========================================
      // STEP 4: FALLBACK - Check v2.0
      // ========================================
      const useNewChatbot = await this.configService.isNewChatbotEnabled();
      
      console.log('[ChatbotWrapper] FALLBACK CHECK - v2.0:', useNewChatbot);
      
      if (useNewChatbot) {
        console.log('[ChatbotWrapper] ✅ Loading v2.0 chatbot');
        await this.loadV2Component();
      } else {
        console.log('[ChatbotWrapper] ❌ v2.0 disabled → Loading v1.x');
        await this.loadV1Component();
      }

    } catch (error: any) {
      this.loggerService.logError(this.ChatbotWrapperComponentError(error), "chatbot-wrapper");
      
      // Check for authentication errors
      const authError = this.configService.hasFeatureConfigAuthError();
      if (authError) {
        console.error('[ChatbotWrapper] Auth error in catch block');
        this.hasAuthError = true;
        this.isLoading = false;
        return;
      }
      
      // Fallback to v1.x on any error
      console.error('[ChatbotWrapper] Error occurred, falling back to v1.x');
      await this.loadV1Component();
    }
  }

  /**
   * Load v1.x (Old Chatbot Component)
   */
  private async loadV1Component(): Promise<void> {
    this.brandThemeService.clearAgentsBrandFromBody();
    console.log('[ChatbotWrapper] 📦 Loading v1.x UI component');
    
    const textAreaHeight = 44;
    AutoResizeTextareaDirective.setUseNewChatbot(textAreaHeight);
    ContenteditableModelDirective.setUseNewChatbot(textAreaHeight);
    
    this.isLoading = false;
    
    setTimeout(() => {
      if (this.container) {
        this.container.clear();
        const factory = this.resolver.resolveComponentFactory(ChatbotOldComponent);
        this.container.createComponent(factory);
        console.log('[ChatbotWrapper] ✅ v1.x loaded successfully');
      }
    }, 0);
  }

  /**
   * Load v2.0 (Modern Chatbot Component)
   */
  private async loadV2Component(): Promise<void> {
    this.brandThemeService.clearAgentsBrandFromBody();
    console.log('[ChatbotWrapper] 📦 Loading v2.0 UI component');
    
    const textAreaHeight = 44;
    let language: any, privileges: any, DomainID: number, URFModuleId: number, firstTimeLoginDetails: any;
    
    // Prepare data needed for v2.0
    const [privilegesResult, languageResult] = await Promise.all([
      this.GetPrivileges(),
      this.callLanguageChangeAPI(),
    ]);
    
    [privileges, DomainID, URFModuleId] = privilegesResult as [any, number, number];
    language = languageResult;

    await this.applyLanguage(language);
    
    // Get wizard tour details
    try {
      const checkWizardTourAPIResponse = await this.callCheckWizardTour(environment.LoginUserID, environment.TenantId);
      firstTimeLoginDetails = {
        "introduction_wizard_enabled": checkWizardTourAPIResponse.application_wizard_details.introduction_wizard.wizard_enabled,
        "tour_functionality_enabled": checkWizardTourAPIResponse.tour_functionality_details.landing_page_tour.tour_enabled,
        "tour_completion_status": checkWizardTourAPIResponse.tour_functionality_details.landing_page_tour.tour_status,
        "wizard_completion_status": checkWizardTourAPIResponse.application_wizard_details.introduction_wizard.wizard_status
      };
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotWrapperComponentError(error), "checkWizardTourAPI");
    }
    
    // Fetch enhancement prompts
    try {
      await firstValueFrom(this.chatbotService.fetchResponseEnhancementPrompts(language));
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotWrapperComponentError(error), "fetchResponseEnhancementPrompts");
    }
    
    // Generate RegRadar questions
    try {
      await this.callGenerateRegRadarAPI(
        environment.LoginUserID,
        environment.TenantId,
        DomainID.toString(),
        "",
        environment.DomainName,
        "Regulations"
      );
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotWrapperComponentError(error), "generateRegRadarAPI");
    }
    
    AutoResizeTextareaDirective.setUseNewChatbot(textAreaHeight);
    ContenteditableModelDirective.setUseNewChatbot(textAreaHeight);
    
    this.isLoading = false;
    
    setTimeout(() => {
      if (this.container) {
        this.container.clear();
        const factory = this.resolver.resolveComponentFactory(ChatbotComponent);
        const componentRef = this.container.createComponent(factory);
        
        if (componentRef.instance && 'setFirstTimeLoginDetails' in componentRef.instance) {
          (componentRef.instance as any).setFirstTimeLoginDetails(firstTimeLoginDetails);
        }
        if (componentRef.instance && 'setPrivilegesAndIds' in componentRef.instance) {
          (componentRef.instance as any).setPrivilegesAndIds(privileges, DomainID, URFModuleId);
        }
        console.log('[ChatbotWrapper] ✅ v2.0 loaded successfully');
      }
    }, 0);
  }

  /**
   * Load v3.0 (Agentic UI Component)
   * @param availableAgentIds - Array of agent IDs that passed all validation checks
   */
  private async loadV3Component(availableAgentIds: string[]): Promise<void> {
    console.log('[ChatbotWrapper] 📦 Loading v3.0 Agentic UI with agents:', availableAgentIds);
    
    const userPrivileges = this.agentAccessControlService.getUserPrivileges();

    // Set privileges for Regulation Next INS_id redirect (gri.regulation.read)
    this.regulationNextService.setPrivileges(userPrivileges);
    this.workbenchAccessService.setPrivileges(userPrivileges);

    // Fetch URF session language and workbench-access flag in parallel.
    // The URF preference (GET {URF_API}/common-uam/session/preference/language)
    // is the same source v2 reads from on boot — without this v3 always renders
    // in the LanguageChangeService default ('en'), even when URF/the host portal
    // has the user pinned to e.g. 'jp' or 'zhCN'. Applying it here also lets the
    // existing onLangChange subscription in AgentMainAreaComponent forward the
    // value (and any later in-app switch) to the embedded Workbench iframe.
    const [workbenchFeatureOn, language] = await Promise.all([
      this.configService.isWorkbenchAccessEnabled(),
      this.callLanguageChangeAPI() as Promise<string>
    ]);

    await this.applyLanguage(language);

    const workbenchAllowed = workbenchFeatureOn && this.workbenchAccessService.hasWorkbenchAccess();
    console.log('[ChatbotWrapper] Workbench (feature ∩ privilege):', { workbenchFeatureOn, workbenchAllowed });
    console.log('[ChatbotWrapper] Applied URF language for v3:', language);

    // Same generate-RegRadar boot step as v2 when regulations agent is available.
    await this.generateRegRadarOnStartupIfNeeded(availableAgentIds);
    
    // IMPORTANT: Set allowed agent IDs BEFORE creating the component
    // This ensures child components (like AgentSelectorComponent) get filtered agents
    // when they call agentHubService.getAllAgents() during their ngOnInit
    this.agentHubService.setAllowedAgentIds(availableAgentIds);
    console.log('[ChatbotWrapper] ✓ Set allowed agent IDs on AgentHubService');

    this.isLoading = false;
    
    setTimeout(async () => {
      if (this.container) {
        this.container.clear();
        
        // Create and load AgentWorkspaceComponent
        const factory = this.resolver.resolveComponentFactory(AgentWorkspaceComponent);
        const componentRef = this.container.createComponent(factory);
        const agentsHost = componentRef.location.nativeElement as HTMLElement;

        await this.brandThemeService.loadAndApplyToAgents(agentsHost, availableAgentIds);
        console.log('[ChatbotWrapper] ✓ Brand theme applied to agents UI');
        
        // Also pass to component instance for reference (redundant but keeps API consistent)
        if (componentRef.instance && 'setAvailableAgents' in componentRef.instance) {
          (componentRef.instance as any).setAvailableAgents(availableAgentIds);
        }
        if (componentRef.instance && 'setWorkbenchAccessAllowed' in componentRef.instance) {
          (componentRef.instance as AgentWorkspaceComponent).setWorkbenchAccessAllowed(workbenchAllowed);
        }

        this.applyWorkbenchDeepLink(componentRef, workbenchAllowed);
        
        console.log('[ChatbotWrapper] ✅ v3.0 AgentWorkspaceComponent loaded successfully');
      }
    }, 0);
  }

  /**
   * When Freya Fusion loads chatbot with ?route=workbench, auto-open AI Workbench after v3 boot.
   */
  private applyWorkbenchDeepLink(
    componentRef: ComponentRef<AgentWorkspaceComponent>,
    workbenchAllowed: boolean
  ): void {
    const params = new URLSearchParams(window.location.search);
    const route = params.get(WORKBENCH_DEEP_LINK_QUERY_KEY);

    if (route !== WORKBENCH_DEEP_LINK_ROUTE_VALUE) {
      return;
    }

    params.delete(WORKBENCH_DEEP_LINK_QUERY_KEY);
    const cleanSearch = params.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', cleanUrl);

    if (!workbenchAllowed) {
      this.loggerService.logInfo(
        '[ChatbotWrapper] workbench deep link denied (feature ∩ privilege)',
        'applyWorkbenchDeepLink'
      );
      return;
    }

    componentRef.instance.openWorkbenchFromDeepLink();
  }

  callCheckWizardTour(userId: string, tenantId: string): Promise<any> {
    return new Promise((resolve) => {
        this.chatbotService.checkWizardTourAPI(userId, tenantId).subscribe({
            next: (response: any) => {
                resolve(response); // Resolve with the response data
            },
            error: (error: any) => {
              const fallbackFirstTimeLoginDetails = {
                "application_wizard_details": {
                  "introduction_wizard": {
                    "wizard_enabled": true,
                    "wizard_status": "completed"
                  }
                },
                "tour_functionality_details": {
                  "landing_page_tour": {
                    "tour_enabled": true,
                    "tour_status": "completed"
                  }
                }
              };
              this.loggerService.logError(this.ChatbotWrapperComponentError(error), "checkWizardTourAPI");
              resolve(fallbackFirstTimeLoginDetails);
            }
        });
    });
}

  /**
   * V3 only: POST generate-regradar-questions when regulations_agent is available.
   * Uses {@link ChatbotService.generateRegRadarAPIForAgenticUI} (does not touch v2 generateRegRadarAPI).
   */
  private async generateRegRadarOnStartupIfNeeded(availableAgentIds: string[]): Promise<void> {
    if (!availableAgentIds.includes(this.regulationsAgentId)) {
      return;
    }

    try {
      await firstValueFrom(
        this.chatbotService.generateRegRadarAPIForAgenticUI(
          environment.TenantId,
          environment.DomainName,
          this.regulationsModuleName
        )
      );
    } catch (error: any) {
      this.loggerService.logError(this.ChatbotWrapperComponentError(error), 'generateRegRadarAPIForAgenticUI');
    }
  }

  callGenerateRegRadarAPI(userId: string,
    tenantId: string,
    domainId: string,
    sessionId: string,
    domainName: string,
    moduleName: string): Promise<void> {
    try{
    return new Promise((resolve, reject) => {
      this.chatbotService.generateRegRadarAPI(userId, tenantId, domainId, sessionId, domainName, moduleName).subscribe({
        next: (data: any) => {
          resolve(data);
        },
        error: (error: any) => {
          this.loggerService.logError(this.ChatbotWrapperComponentError(error), "generateRegRadarAPI");
          resolve(); // Continue instead of rejecting
        }
      });
    });
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotWrapperComponentError(error), "generateRegRadarAPI");
    return Promise.reject(error);
  }
  }

  /**
   * Apply a resolved i18n key via `TranslateService` and persist it.
   *
   * Shared by v2 and v3 bootstrap: both fetch the language from URF
   * (`callLanguageChangeAPI()` → `GET {URF_API}/common-uam/session/preference/language`)
   * and must commit it to `TranslateService` before mounting their UI so the
   * first paint is in the user's preferred locale and downstream listeners
   * (e.g. `AgentMainAreaComponent.onLangChange` → Workbench iframe) start
   * with the correct value.
   *
   * Reloads the translation file first so the bundle matches the release version
   * set on ConfigurationService (V2 vs V3). Without reload, ngx-translate reuses
   * a cached bundle from an earlier bootstrap step. Errors are swallowed and logged —
   * i18n must never block bootstrap.
   */
  private applyLanguage(language: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.translate.reloadLang(language).subscribe({
        next: () => {
          this.translate.use(language).subscribe({
            next: () => {
              try { localStorage.setItem('language', language); } catch { /* storage may be unavailable */ }
              resolve();
            },
            error: (error) => {
              this.loggerService.logError(this.ChatbotWrapperComponentError(error), "changeLanguageAPI");
              resolve();
            }
          });
        },
        error: (error) => {
          this.loggerService.logError(this.ChatbotWrapperComponentError(error), "changeLanguageAPI");
          this.translate.use(language).subscribe({
            next: () => {
              try { localStorage.setItem('language', language); } catch { /* storage may be unavailable */ }
              resolve();
            },
            error: () => resolve()
          });
        }
      });
    });
  }

  callLanguageChangeAPI(){
    return new Promise((resolve) => {
      this.chatbotService.changeLanguageAPI().subscribe({
          next: (response: any) => {
            if (response.body?.data == null || response.body?.data === "" || !this.supportedLanguages.includes(response.body?.data)) {
              resolve("en"); // Default to 'en'
            } 
            else {
                resolve(response.body.data); // Resolve with the response data
            }
          },
          error: (error: any) => {
              this.loggerService.logError(this.ChatbotWrapperComponentError(error), "changeLanguageAPI");
              resolve("en"); // Default to 'en' on error
          }
      });
  });
  }


GetURFModuleId(DomainID: number): Promise<number> { // Accept DomainID as a parameter
  try{
  let URFModuleId: number; // Declare URFModuleId with let
  
  if (environment.IsDevelopment) {
    URFModuleId = 4; // Set default for development
    return Promise.resolve(URFModuleId);
  } else {
    return new Promise((resolve, reject) => {
      this.chatbotService.GetURFModuleId(DomainID).subscribe((data: any) => {
        if (data && data.code === 200 && Array.isArray(data.data)) {
          const filteredItems = data.data.filter((item: any) => item.displayName === 'Freya');
          URFModuleId = filteredItems.length > 0 ? parseInt(filteredItems[0].id) : 0;
        } else {
          URFModuleId = 0;
        }
        resolve(URFModuleId); // Resolve with the URFModuleId
      }, error => {
        URFModuleId = 0;
        this.loggerService.logError(this.ChatbotWrapperComponentError(error), "GetURFModuleId");
        reject(error); // Reject the promise in case of an error
      });
    });
  }
}
catch(error: any){
  this.loggerService.logError(this.ChatbotWrapperComponentError(error), "GetURFModuleId");
  return Promise.reject(error);
}
}

GetDomainAndModuleId(): Promise<{ DomainID: number; URFModuleId: number }> {
  try{
  let DomainID: number;
  if (environment.IsDevelopment) {
    DomainID = 1;
    return Promise.resolve({ DomainID, URFModuleId: 4 }); // Set default for development
  } else {
    return new Promise((resolve, reject) => {
      this.chatbotService.GetURFAllDomains().subscribe((data: any) => {
        if (data && data.code === 200 && Array.isArray(data.data)) {
          const filteredItems = data.data.filter((item: any) => item.domainPhrase === environment.DomainName);
          DomainID = filteredItems.length > 0 ? parseInt(filteredItems[0].value) : 0;
        } else {
          DomainID = 0;
        }

        // Now call GetURFModuleId with the DomainID
        this.GetURFModuleId(DomainID).then((URFModuleId) => {
          resolve({ DomainID, URFModuleId }); // Use the returned URFModuleId
        }).catch(error => {
          this.loggerService.logError(this.ChatbotWrapperComponentError(error), "GetURFModuleId");
          reject(error);
        });
      }, error => {
        DomainID = 0;
        this.loggerService.logError(this.ChatbotWrapperComponentError(error), "GetURFModuleId");
        reject(error);
      });
    });
  }
  }
  catch(error: any){
    this.loggerService.logError(this.ChatbotWrapperComponentError(error), "GetDomainAndModuleId");
    return Promise.reject(error);
  }
}

async GetPrivileges(){
  const { DomainID, URFModuleId } = await this.GetDomainAndModuleId();
  const privileges = await this.privilegesService.getPrivilegesList(environment.LoginUserID, URFModuleId, DomainID);
  return [privileges, DomainID, URFModuleId];
}
}